import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, Field } from "@/components/Atoms";
import { PayoutFlash } from "@/components/PayoutFlash";
import { Hammer, Send, Trophy, Coins, Star } from "lucide-react";
import { PalPortrait } from "@/components/PalAssets";

export default function Economy({ embedded = false }) {
  const { user, refreshProfile } = useAuth();
  const toast = useToast();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("work");

  const load = async () => {
    try {
      const { data } = await api.get("/economy/leaderboard?limit=50");
      setBoard(data.entries || []);
    } catch (e) {
      // Surface real failures so users aren't left with an empty leaderboard silently.
      console.error("leaderboard load failed", e);
      toast.error(apiError(e, "Couldn't load leaderboard"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      {!embedded && (
        <PageHeader title="The Economy" subtitle="Earn, transfer, and rule the leaderboard." right={<Currency value={user?.points} className="text-2xl" />} />
      )}
      <div className="flex gap-2 mb-6 border-b border-white/5 overflow-x-auto" data-testid="economy-tabs">
        {[["work", Hammer, "Work"], ["transfer", Send, "Transfer"], ["leaderboard", Trophy, "Leaderboard"]].map(([k, Icon, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`economy-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              tab === k ? "border-[var(--teal)] text-[var(--teal)]" : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "work" && <WorkTab onUpdate={refreshProfile} />}
      {tab === "transfer" && <TransferTab onUpdate={refreshProfile} />}
      {tab === "leaderboard" && (loading ? <Loader /> : <LeaderboardTab entries={board} />)}
    </div>
  );
}

function WorkTab({ onUpdate }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // While the firefly clip is playing we hold the API payload in
  // `pendingResult` and DON'T fire the toast / coin-flash / "Last
  // earned" line until the video reaches its end. This keeps the
  // reveal cinematic — the payout drops in sync with the clip's
  // final frame instead of beating it to the punch.
  const [pendingResult, setPendingResult] = useState(null);
  const [cdExpires, setCdExpires] = useState(null);
  const [now, setNow] = useState(Date.now());
  // Coin-burst cinematic — fires on every successful work payout so
  // mining feels rewarding, same component the gambling/PvP/towers
  // wins use for consistency across the site.
  const [flash, setFlash] = useState(null);
  // Pal picker — must select a pal before working. Better workers
  // (high WorkSuitability + work-flavoured passives + lucky) earn
  // a multiplier on top of the random base payout.
  const [pals, setPals] = useState([]);
  const [palsLoading, setPalsLoading] = useState(true);
  const [selectedPal, setSelectedPal] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchAll = () =>
      api.get("/economy/work/pals").then(({ data }) => {
        const pp = data.pals || [];
        setPals(pp);
        // Default to the best worker so the picker doesn't feel like a chore.
        setSelectedPal((cur) => cur || pp[0]?.pal_name || null);
        setCdExpires(data.cooldown_expires ? new Date(data.cooldown_expires).getTime() : null);
      }).catch(() => {}).finally(() => setPalsLoading(false));
    fetchAll();
    const t = setInterval(() => setNow(Date.now()), 1000);
    // Live cooldown updates — when an admin shortens/clears the work
    // cooldown setting, AuthContext dispatches `chillets:cooldown-update`
    // over the WebSocket. We just re-pull our row.
    const onCdUpdate = (e) => {
      const { key } = e.detail || {};
      if (!key || key === "cooldown_work") fetchAll();
    };
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => {
      clearInterval(t);
      window.removeEventListener("chillets:cooldown-update", onCdUpdate);
      window.clearTimeout(window.__work_reveal_t);
    };
  }, []);

  const work = async () => {
    if (!selectedPal) {
      toast.error("Pick a pal first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/economy/work", { pal_name: selectedPal });
      // Update cooldown right away so the button shows "Cooldown 59:59"
      // while the firefly clip plays, but stash the payout in
      // `pendingResult` — the toast, coin-burst, and "Last earned" line
      // are deferred to `revealReward()` (called from <video onEnded>).
      setPendingResult(data);
      setResult(null);
      setCdExpires(new Date(data.cooldown_expires).getTime());
      onUpdate?.();
      // Safety net — if the video never fires `onEnded` (autoplay
      // blocked, codec error, network drop), still reveal the reward
      // after 8 s so the user is never stuck in "Working…".
      window.clearTimeout(window.__work_reveal_t);
      window.__work_reveal_t = window.setTimeout(() => revealReward(), 8000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.expires_at) setCdExpires(new Date(detail.expires_at).getTime());
      toast.error(detail?.message || apiError(e));
    } finally { setBusy(false); }
  };

  // Fires once the firefly clip finishes playing. Promotes the
  // pending payout into the visible `result`, triggers the toast +
  // coin-burst cinematic so the reveal lands on the clip's last frame.
  const revealReward = () => {
    window.clearTimeout(window.__work_reveal_t);
    setPendingResult((p) => {
      if (!p) return null;
      const isBonus = !!p.bonus_vein;
      const mult = p.bonus_multiplier || 1;
      const wMult = p.work_score?.multiplier || 1;
      toast.success(
        isBonus
          ? `💥 GOLD VEIN! +${p.earned} Chillet Coins (${mult}×)`
          : `+${p.earned} Chillet Coins · ${selectedPal} ×${wMult.toFixed(2)}`
      );
      setFlash({
        amount: p.earned,
        label: isBonus ? `GOLD VEIN · ${mult}×` : "MINED",
        bonus: isBonus,
      });
      setResult(p);
      return null;
    });
  };

  const remain = cdExpires ? Math.max(0, cdExpires - now) : 0;
  const min = Math.floor(remain / 60000);
  const sec = Math.floor((remain % 60000) / 1000);
  const onCooldown = remain > 0;
  const picked = pals.find((p) => p.pal_name === selectedPal);

  return (
    <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-8">
      {/* ──────────────────────────────────────────────────────────────
          Work card. Rebuilt 2026-06-19 (build v4) — flex column on
          mobile, grid on lg+. No inline-style sizing, no aspect-ratio
          tricks — only standard Tailwind utilities so every viewport
          renders the same way.
          ────────────────────────────────────────────────────────────── */}
      <div className="glass-card p-4 sm:p-7 lg:p-10 text-center" data-testid="work-card">
        <Hammer
          size={48}
          className="text-[var(--teal)] mx-auto mb-3 lg:mb-5"
          style={{ filter: "drop-shadow(0 0 14px var(--teal-glow))" }}
        />
        <h3 className="text-xl sm:text-2xl lg:text-3xl lg:tracking-wide mb-1 lg:mb-2">Work the Mines</h3>
        <p className="text-sm lg:text-base text-[var(--muted)] mb-5 lg:mb-7 lg:max-w-md lg:mx-auto">
          Send a pal to work — suitability, passives, and Lucky bonus
          all boost the haul.
        </p>

        {/* Firefly poster — static <img> first so the layout box can
            never collapse to zero. Swaps to <video> on a successful
            work tick to play the celebration clip, then settles on
            its last frame while the reward is revealed. Larger and
            more cinematic on desktop via lg: modifiers. */}
        <div className="mx-auto mb-5 lg:mb-7 w-full max-w-[320px] lg:max-w-md aspect-video rounded-lg lg:rounded-xl overflow-hidden border border-cyan-400/30 lg:border-cyan-400/40 lg:shadow-[0_0_28px_rgba(0,229,255,0.18)] bg-black/40">
          {(pendingResult || result) && (
            <video
              ref={videoRef}
              key={(pendingResult || result).cooldown_expires}
              src="/assets/work/firefly_work.mp4"
              poster="/assets/work/firefly_work_poster.jpg"
              autoPlay
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
              onEnded={revealReward}
              className="w-full h-full object-cover pointer-events-none select-none"
              data-testid="work-video"
            />
          )}
          {!pendingResult && !result && (
            <img
              src="/assets/work/firefly_work_poster.jpg"
              alt="Work the mines"
              className="w-full h-full object-cover pointer-events-none select-none"
              data-testid="work-poster"
            />
          )}
        </div>

        <WorkPalPicker
          pals={pals}
          loading={palsLoading}
          value={selectedPal}
          onChange={setSelectedPal}
        />

        <button
          onClick={work}
          disabled={busy || pendingResult || onCooldown || !selectedPal}
          className="btn btn-lg mt-4 lg:mt-6 w-full lg:w-auto lg:min-w-[260px] lg:px-10"
          data-testid="work-button"
        >
          {pendingResult || busy
            ? "Working…"
            : onCooldown
              ? `Cooldown ${min}:${String(sec).padStart(2, "0")}`
              : selectedPal
                ? `Send ${selectedPal} to work`
                : pals.length === 0 ? "Catch a pal first" : "Select a pal"}
        </button>

        {picked && (
          <WorkScoreBreakdown pal={picked} />
        )}

        {result && (
          <div className="mt-4 text-sm" data-testid="work-result">
            Last earned: <span className="glow-teal text-lg">+{result.earned}</span>{" "}
            <span className="text-[var(--muted)]">
              (base {result.base_amount} × {(result.work_score?.multiplier || 1).toFixed(2)})
            </span>
          </div>
        )}
      </div>

      {/* How-it-works panel. On desktop, sits as the right column with
          extra padding + a subtle gold accent on the Gold-Vein line so
          the rules card has its own visual rhythm. */}
      <div className="glass-card p-4 sm:p-7 lg:p-10 lg:self-start">
        <h3 className="label mb-3 lg:mb-5 lg:text-base lg:tracking-[0.22em]">How it works</h3>
        <ul className="text-sm lg:text-base text-[var(--muted)] space-y-2 lg:space-y-3 lg:leading-relaxed">
          <li>• 60-minute cooldown between work runs.</li>
          <li>• Each Pal's best <span className="text-[var(--bone)]">work suitability</span> (Mining, Lumbering, Handiwork, etc.) sets the base — L1 = ×1.15, L4 = ×1.60.</li>
          <li>• Work-flavoured passives (<span className="text-emerald-300">Workaholic</span>, <span className="text-emerald-300">Artisan</span>, <span className="text-emerald-300">Serious</span>) add on top — negative passives <span className="text-red-300">subtract</span>.</li>
          <li>• ✨ Lucky pals earn an extra +10 % flat bonus.</li>
          <li>• 3 % chance of a <span className="text-yellow-300 lg:glow-gold">Gold Vein</span> on top of everything for a 2-3× spike.</li>
        </ul>
      </div>

      <PayoutFlash
        show={flash !== null}
        variant="skull"
        amount={flash?.amount || 0}
        label={flash?.label || "MINED"}
        bonus={flash?.bonus || false}
        onDone={() => setFlash(null)}
      />
    </div>
  );
}

function TransferTab({ onUpdate }) {
  const toast = useToast();
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!to || !amount) return;
    if (!window.confirm(`Send ${amount} skulls to ${to}? You'll be charged a 5% tax on top.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/economy/transfer", { to_user_id: to, amount: parseInt(amount, 10) });
      toast.success(`Sent ${data.sent}. Tax ${data.tax}.`);
      setTo(""); setAmount("");
      onUpdate?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const tax = Math.max(1, Math.floor((parseInt(amount, 10) || 0) * 0.05));

  return (
    <div className="max-w-lg mx-auto glass-card p-7 fade-up">
      <h3 className="text-2xl mb-1">Transfer Skulls</h3>
      <p className="text-sm text-[var(--muted)] mb-5">A 5% tax applies to all transfers.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="transfer-form">
        <Field label="Recipient User ID" hint="Their public user_id from the leaderboard.">
          <input className="input" required value={to} onChange={(e) => setTo(e.target.value)} data-testid="transfer-to-input" />
        </Field>
        <Field label="Amount" hint={amount ? `+ ${tax} tax = ${parseInt(amount,10) + tax} total` : ""}>
          <input className="input" required type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="transfer-amount-input" />
        </Field>
        <button className="btn btn-lg w-full" disabled={busy} data-testid="transfer-submit-button">
          <Send size={14} /> {busy ? "Sending…" : "Send Skulls"}
        </button>
      </form>
    </div>
  );
}

function LeaderboardTab({ entries }) {
  return (
    <div className="glass-card p-6 fade-up">
      <table className="w-full" data-testid="leaderboard-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
            <th className="py-2 w-12">#</th>
            <th className="py-2">Player</th>
            <th className="py-2 text-right">Skulls</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const rankClass = e.rank === 1 ? "glow-gold" : e.rank === 2 ? "text-[#c0c0c0]" : e.rank === 3 ? "text-[#cd7f32]" : "";
            return (
              <tr key={e.user_id} className="border-t border-white/5 hover:bg-white/5">
                <td className={`py-3 ${rankClass}`}>{e.rank}</td>
                <td className="py-3 truncate">{e.user_name}</td>
                <td className="py-3 text-right"><Currency value={e.points} /></td>
              </tr>
            );
          })}
          {entries.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-[var(--muted)]">No entries yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Work Pal Picker — horizontal scroll of owned pals, sorted by
// multiplier (best worker first). Click a card to select; the right
// panel shows the live multiplier breakdown for that pick.
function WorkPalPicker({ pals, loading, value, onChange }) {
  if (loading) {
    return <div className="text-xs text-[var(--muted)] py-3 text-center">Loading your pals…</div>;
  }
  if (!pals || pals.length === 0) {
    return (
      <div className="text-xs text-[var(--muted)] py-3 text-center italic"
           data-testid="work-no-pals">
        No pals yet — head to the Pal Game tab to catch one.
      </div>
    );
  }
  return (
    <div className="border border-white/10 rounded-lg p-3 bg-black/20 text-left"
         data-testid="work-pal-picker">
      <div className="flex items-center justify-between mb-2">
        <span className="label text-[10px]">Pick a worker · {pals.length} pals</span>
        <span className="text-[10px] text-[var(--muted)] font-mono">
          best ×{pals[0].multiplier.toFixed(2)}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mb-1" style={{ scrollbarWidth: "thin" }}>
        {pals.slice(0, 60).map((p) => {
          const active = value === p.pal_name;
          return (
            <button
              key={p.pal_name}
              type="button"
              onClick={() => onChange(p.pal_name)}
              className={`flex-shrink-0 w-24 p-2 rounded-md border transition-colors text-left relative ${
                active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.10)] ring-1 ring-[var(--teal)]/40"
                  : "border-white/10 bg-black/20 hover:bg-white/5"
              } ${p.is_lucky ? "ring-1 ring-yellow-400/40" : ""}`}
              data-testid={`work-pal-${p.pal_name}`}
              title={`${p.pal_name} · ×${p.multiplier.toFixed(2)}`}
            >
              {p.is_lucky && (
                <span className="absolute top-0.5 right-0.5 text-[9px] text-yellow-300">✨</span>
              )}
              <PalPortrait name={p.pal_name} size="w-full aspect-square" />
              <div className="text-[10px] truncate mt-1">{p.pal_name}</div>
              <div className="text-[10px] font-mono text-[var(--teal)]">
                ×{p.multiplier.toFixed(2)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Work score breakdown — shows the base/passive/level/lucky math
// for the currently-picked pal so the user knows why their number is
// what it is. Same shape as the `work_score` field on the work POST.
function WorkScoreBreakdown({ pal }) {
  const stars = pal.best_level ? Array.from({ length: pal.best_level }) : [];
  return (
    <div className="mt-4 p-3 bg-black/30 border border-white/10 rounded-lg text-left text-xs"
         data-testid="work-score-breakdown">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
          {pal.pal_name} · Lv {pal.level}
        </span>
        <span className="text-[var(--teal)] text-lg font-mono">×{pal.multiplier.toFixed(2)}</span>
      </div>
      {pal.best_suit ? (
        <div className="flex items-center justify-between py-1 border-t border-white/5">
          <span>Best work · <span className="text-[var(--bone)]">{pal.best_suit}</span></span>
          <span className="flex items-center gap-0.5">
            {stars.map((_, i) => (
              <Star key={i} size={11} fill="currentColor" className="text-[var(--gold)]" />
            ))}
            <span className="ml-1 text-[10px] text-[var(--muted)]">×{pal.base_mult}</span>
          </span>
        </div>
      ) : (
        <div className="py-1 border-t border-white/5 text-[var(--muted)] italic">
          No work suitability — pal contributes nothing extra.
        </div>
      )}
      {pal.passive_pct !== 0 && (
        <div className="flex items-center justify-between py-1 border-t border-white/5">
          <span>Work passives</span>
          <span className={pal.passive_pct >= 0 ? "text-emerald-300" : "text-red-300"}>
            {pal.passive_pct >= 0 ? "+" : ""}{pal.passive_pct}%
          </span>
        </div>
      )}
      {pal.level_pct > 0 && (
        <div className="flex items-center justify-between py-1 border-t border-white/5">
          <span>Level bonus</span>
          <span className="text-emerald-300">+{pal.level_pct}%</span>
        </div>
      )}
      {pal.is_lucky && (
        <div className="flex items-center justify-between py-1 border-t border-white/5">
          <span>✨ Lucky bonus</span>
          <span className="text-yellow-300">+10%</span>
        </div>
      )}
      {Array.isArray(pal.passives) && pal.passives.length > 0 && (
        <div className="pt-2 mt-1 border-t border-white/5 flex flex-wrap gap-1">
          {pal.passives.map((p) => (
            <span key={p}
                  className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/70 uppercase tracking-wider">
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

